<template>
  <div
    v-bind="attrs"
    ref="rootRef"
    class="search-select-input"
    :class="{
      'is-focus': isInputFocus
    }"
  >
    <div class="search-select-input__inner">
      <div
        ref="typeTriggerRef"
        class="search-select-input__type"
        :class="{ 'is-open': showTypePanel }"
        @click.stop="toggleTypePanel"
      >
        <span class="search-select-input__type-label">{{ (currentType && currentType.label) || '请选择类型' }}</span>
        <el-icon class="search-select-input__type-arrow">
          <ArrowDown />
        </el-icon>
      </div>

      <div ref="mainTriggerRef" class="search-select-input__main" @click="focusInput">
        <div v-if="isSelectType && isMultiple" class="search-select-input__tags">
          <span
            v-for="item in visibleSelectedItems"
            :key="item.value"
            class="search-select-input__tag"
          >
            <span class="search-select-input__tag-label">{{ item.label }}</span>
            <el-icon class="search-select-input__tag-close" @click.stop="removeItem(item.value)">
              <Close />
            </el-icon>
          </span>
          <el-tooltip
            v-if="hiddenSelectedCount"
            effect="light"
            placement="bottom"
            popper-class="search-select-input__selected-tooltip"
            :show-after="120"
          >
            <template #content>
              <div class="search-select-input__selected-tooltip-content">
                <span
                  v-for="item in hiddenSelectedItems"
                  :key="item.value"
                  class="search-select-input__selected-tooltip-item"
                >
                  <span class="search-select-input__selected-tooltip-label">
                    {{ item.label }}
                  </span>
                </span>
              </div>
            </template>
            <span class="search-select-input__tag search-select-input__tag--count">
              +{{ hiddenSelectedCount }}
            </span>
          </el-tooltip>
          <input
            ref="inputRef"
            v-model="keyword"
            class="search-select-input__input search-select-input__input--multiple"
            :placeholder="multiplePlaceholder"
            @focus="handleInputFocus"
            @input="handleKeywordInput"
            @keydown.enter.prevent="handleEnter"
          >
        </div>

        <div v-else-if="isSelectType && !isMultiple" class="search-select-input__single">
          <span
            v-if="singleSelectedItem && !keyword"
            class="search-select-input__tag--single"
          >
            <span class="search-select-input__tag-label">{{ singleSelectedItem.label }}</span>
          </span>
          <input
            ref="inputRef"
            v-model="keyword"
            class="search-select-input__input search-select-input__input--single"
            :placeholder="singlePlaceholder"
            @focus="handleInputFocus"
            @blur="() => keyword = ''"
            @keydown.enter.prevent="handleEnter"
          >
        </div>

        <input
          v-else
          ref="inputRef"
          v-model="keyword"
          class="search-select-input__input"
          :placeholder="currentPlaceholder"
          @focus="handleInputFocus"
          @input="handleKeywordInput"
          @keydown.enter.prevent="handleInputEnter"
        >

        <div class="search-select-input__suffix">
          <el-icon v-if="showClearIcon" @click.stop="clearValue">
            <Close />
          </el-icon>
          <el-icon v-else>
            <Search />
          </el-icon>
        </div>
      </div>
    </div>
  </div>

  <Teleport v-if="teleportTarget" :to="teleportTarget">
    <div
      v-show="showTypePanel"
      ref="typePanelRef"
      class="search-select-input__type-panel"
      :style="typePanelStyle"
    >
      <div
        v-for="item in normalizedTypes"
        :key="item.value"
        class="search-select-input__type-option"
        :class="{ 'is-active': currentType && item.value === currentType.value }"
        @click.stop="changeType(item)"
      >
        <span>{{ item.label }}</span>
        <el-icon v-if="currentType && item.value === currentType.value">
          <Check />
        </el-icon>
      </div>
    </div>
  </Teleport>

  <Teleport v-if="teleportTarget" :to="teleportTarget">
    <div
      v-show="showResultPanel && isSelectType"
      ref="resultPanelRef"
      class="search-select-input__panel"
      :style="resultPanelStyle"
    >
      <div v-if="filteredOptions.length === 0" class="search-select-input__status">
        {{ emptyText }}
      </div>
      <template v-else>
        <div
          v-for="item in visibleFilteredOptions"
          :key="item.value"
          class="search-select-input__option"
          :class="{
            'is-selected': isSelected(item.value)
          }"
          @click.stop="selectItem(item)"
        >
          <span class="search-select-input__option-label">{{ item.label }}</span>
          <el-icon v-if="isSelected(item.value)" class="search-select-input__option-check">
            <Check />
          </el-icon>
        </div>
        <div v-if="hiddenFilteredCount" class="search-select-input__status search-select-input__status--more">
          {{ moreOptionsText }}
        </div>
      </template>
    </div>
  </Teleport>
</template>

<script setup>
import { t } from 'app/i18n'
import { ArrowDown, Check, Close, Search } from '@element-plus/icons-vue'
import { computed, defineEmits, defineProps, nextTick, onBeforeUnmount, onMounted, ref, useAttrs, watch } from 'vue'

// 对外统一使用 { value, type } 结构，方便父组件同时拿到当前类型和对应的输入结果。
const props = defineProps({
  modelValue: {
    type: Object,
    default: undefined
  },
  value: {
    type: Object,
    default: () => ({
      value: '',
      type: ''
    })
  },
  types: {
    type: Array,
    default: () => []
  },
  appendTo: {
    type: [String, Object],
    default: 'body'
  },
  placeholder: {
    type: String,
    default: '请输入内容'
  }
})

const emit = defineEmits(['update:modelValue', 'update:value', 'change', 'enter'])
const attrs = useAttrs()

// 根节点和两个触发区分别用于外部点击判断、Teleport 面板定位计算。
const rootRef = ref(null)
const typeTriggerRef = ref(null)
const mainTriggerRef = ref(null)
const typePanelRef = ref(null)
const resultPanelRef = ref(null)
const inputRef = ref(null)
// keyword 既承担 input 类型的真实输入值，也承担 select 类型的前端过滤关键词。
const keyword = ref('')
const isInputFocus = ref(false)
const showTypePanel = ref(false)
const showResultPanel = ref(false)
// selectedItems 始终保存标准化后的 `{ label, value }` 结构，便于单选/多选统一处理。
const selectedItems = ref([])
const currentType = ref(null)
// 下拉通过 Teleport 渲染到目标容器，避免被父级 `overflow: hidden` 截断。
const teleportTarget = ref('body')
const typePanelStyle = ref({})
const resultPanelStyle = ref({})
// 所有下拉面板的视觉高度上限，定位计算和样式都复用这一个常量。
const PANEL_MAX_HEIGHT = 220
// 大量 options 一次性渲染会拖慢选择反馈，限制首屏渲染数量并保留关键词筛选能力。
const OPTION_RENDER_LIMIT = 1000
// 同时兼容 `v-model` 和 `v-model:value` 两种写法，组件内部统一从这里取值。
const currentModelValue = computed(() => (props.modelValue !== undefined ? props.modelValue : props.value))
// 每个类型项都会被规整成统一结构，避免模板和逻辑层反复兜底判断。
const normalizedTypes = computed(() => props.types
  .filter(item => item && item.value)
  .map(item => ({
    ...item,
    type: item.type === 'input' ? 'input' : 'select',
    mode: item.mode === 'multiple' ? 'multiple' : 'single',
    options: Array.isArray(item.options) ? item.options : []
  })))

// 只有 select 类型才区分 single / multiple，input 类型始终按单值处理。
const getTypeMode = typeItem => {
  if (!typeItem || typeItem.type !== 'select') return 'single'
  return typeItem.mode === 'multiple' ? 'multiple' : 'single'
}

// 将当前类型拆成 input / select 两条交互分支，后续逻辑直接复用。
const isTypeInput = typeItem => Boolean(typeItem && typeItem.type === 'input')
const isTypeSelect = typeItem => !isTypeInput(typeItem)

const isSelectType = computed(() => isTypeSelect(currentType.value))
const isMultiple = computed(() => getTypeMode(currentType.value) === 'multiple')
const singleSelectedItem = computed(() => {
  if (!isSelectType.value || isMultiple.value) return null
  return selectedItems.value[0] || null
})
const selectedValueSet = computed(() => new Set(selectedItems.value.map(item => item.value)))
const visibleSelectedItems = computed(() => {
  if (!isMultiple.value) return []
  return selectedItems.value.slice(0, 1)
})
const hiddenSelectedItems = computed(() => {
  if (!isMultiple.value) return []
  return selectedItems.value.slice(1)
})
const hiddenSelectedCount = computed(() => {
  if (!isMultiple.value || selectedItems.value.length <= 1) return 0
  return selectedItems.value.length - 1
})
const showClearIcon = computed(() => {
  if (isSelectType.value && isMultiple.value) {
    return Boolean(keyword.value || selectedItems.value.length)
  }
  return Boolean(keyword.value || (isSelectType.value && selectedItems.value.length))
})
const currentPlaceholder = computed(() => {
  if (currentType.value && currentType.value.placeholder) return currentType.value.placeholder
  return props.placeholder
})
const multiplePlaceholder = computed(() => {
  if (selectedItems.value.length) return ''
  return currentPlaceholder.value
})
const singlePlaceholder = computed(() => {
  if (singleSelectedItem.value) return ''
  return currentPlaceholder.value
})
const currentOptions = computed(() => normalizeOptions(currentType.value ? currentType.value.options : []))
const filteredOptions = computed(() => {
  if (!isSelectType.value) return []

  const query = keyword.value.trim().toLowerCase()
  if (!query) return currentOptions.value

  return currentOptions.value.filter(item => {
    const label = item.label.toLowerCase()
    const value = item.value.toLowerCase()
    return label.includes(query) || value.includes(query)
  })
})
const visibleFilteredOptions = computed(() => filteredOptions.value.slice(0, OPTION_RENDER_LIMIT))
const hiddenFilteredCount = computed(() => Math.max(filteredOptions.value.length - visibleFilteredOptions.value.length, 0))
const emptyText = computed(() => {
  if (!currentOptions.value.length) return t('report.analyse.summary.search_select.empty')
  return t('report.analyse.summary.search_select.match.empty')
})
const moreOptionsText = computed(() => {
  if (keyword.value.trim()) {
    return t('report.analyse.summary.search_select.more.match.description', [visibleFilteredOptions.value.length, hiddenFilteredCount.value])
  }

  return t('report.analyse.summary.search_select.more.description', [visibleFilteredOptions.value.length])
})

// 只要任一面板可见，就需要监听滚动/resize 并持续校准 Teleport 后的位置。
const isAnyPanelVisible = computed(() => showTypePanel.value || (showResultPanel.value && isSelectType.value))

// appendTo 支持 CSS 选择器或直接传入 DOM，最终统一解析成 Teleport 目标节点。
const resolveTeleportTarget = () => {
  if (typeof window === 'undefined') return 'body'

  if (typeof props.appendTo === 'string') {
    return document.querySelector(props.appendTo) || document.body
  }

  if (props.appendTo && props.appendTo.nodeType === 1) {
    return props.appendTo
  }

  return document.body
}

// Teleport 面板不再依赖父级布局，而是基于触发元素实时计算 fixed 定位；
// 如果下方空间不足且上方更充足，则自动向上展开，同时把最大高度钉死在限制范围内。
const createPanelStyle = (triggerEl, panelEl, fallbackHeight = PANEL_MAX_HEIGHT) => {
  if (!triggerEl) return {}

  const rect = triggerEl.getBoundingClientRect()
  const panelWidth = Math.max(rect.width, 110)
  const viewportPadding = 8
  const panelGap = 4
  const preferredHeight = Math.min(panelEl?.scrollHeight || fallbackHeight, PANEL_MAX_HEIGHT)
  const bottomSpace = Math.max(window.innerHeight - rect.bottom - panelGap - viewportPadding, 0)
  const topSpace = Math.max(rect.top - panelGap - viewportPadding, 0)
  const shouldOpenTop = bottomSpace < preferredHeight && topSpace > bottomSpace
  const availableHeight = shouldOpenTop ? topSpace : bottomSpace
  const visibleHeight = Math.min(preferredHeight, availableHeight || preferredHeight)
  const top = shouldOpenTop
    ? Math.max(rect.top - panelGap - visibleHeight, viewportPadding)
    : Math.min(rect.bottom + panelGap, window.innerHeight - viewportPadding)
  const maxLeft = Math.max(window.innerWidth - panelWidth - viewportPadding, viewportPadding)
  const left = Math.min(Math.max(rect.left, viewportPadding), maxLeft)

  return {
    position: 'fixed',
    top: `${top}px`,
    left: `${left}px`,
    width: `${panelWidth}px`,
    maxHeight: `${Math.max(Math.min(Math.floor(availableHeight), PANEL_MAX_HEIGHT), 0)}px`,
    transformOrigin: shouldOpenTop ? 'bottom left' : 'top left',
    zIndex: 2005
  }
}

// 两类面板的定位锚点不同：类型面板依赖左侧类型触发区，结果面板依赖右侧输入区。
const syncPanelPosition = () => {
  if (showTypePanel.value) {
    typePanelStyle.value = createPanelStyle(typeTriggerRef.value, typePanelRef.value, PANEL_MAX_HEIGHT)
  }

  if (showResultPanel.value && isSelectType.value) {
    resultPanelStyle.value = createPanelStyle(mainTriggerRef.value, resultPanelRef.value, PANEL_MAX_HEIGHT)
  }
}

// 等待 DOM 和 Teleport 内容都更新到位后再取尺寸，避免拿到旧高度导致定位偏差。
const scheduleSyncPanelPosition = () => {
  nextTick(() => {
    requestAnimationFrame(() => {
      syncPanelPosition()
    })
  })
}

// 切换类型时返回该类型对应的空值结构，保证父组件拿到的数据始终合法。
const createEmptyPayload = typeItem => ({
  type: (typeItem && typeItem.value) || (currentType.value && currentType.value.value) || '',
  value: getTypeMode(typeItem || currentType.value) === 'multiple' ? [] : ''
})

// 按当前类型把外部 value 归一化成组件内部使用的单值或数组。
const normalizeTypePayload = (payload, typeItem) => {
  const rawValue = payload && payload.value

  if (isTypeInput(typeItem)) {
    return Array.isArray(rawValue) ? (rawValue[0] || '') : (rawValue || '')
  }

  if (getTypeMode(typeItem) === 'multiple') {
    return Array.isArray(rawValue) ? rawValue : rawValue ? [rawValue] : []
  }

  return Array.isArray(rawValue) ? (rawValue[0] || '') : (rawValue || '')
}

// 兼容 options 中不同字段命名，统一提取成组件内部的 label/value。
const getNormalizedOption = item => {
  if (item === null || item === undefined) return null

  if (typeof item !== 'object') {
    return {
      label: String(item),
      value: String(item),
      raw: item
    }
  }

  const label = item.label !== undefined
    ? item.label
    : item.name !== undefined
      ? item.name
      : item.title !== undefined
        ? item.title
        : item.text !== undefined
          ? item.text
          : item.value !== undefined
            ? item.value
            : item.id
  const value = item.value !== undefined
    ? item.value
    : item.id !== undefined
      ? item.id
      : item.key !== undefined
        ? item.key
        : item.code !== undefined
          ? item.code
          : label

  if (label === undefined || value === undefined) return null

  return {
    label: String(label),
    value: String(value),
    raw: item
  }
}

// 对下拉 options 做统一清洗和去重，确保渲染层只处理标准结构。
const normalizeOptions = list => {
  const seenValues = new Set()

  return (Array.isArray(list) ? list : []).reduce((prev, item) => {
    const normalizedItem = getNormalizedOption(item)

    if (!normalizedItem || seenValues.has(normalizedItem.value)) return prev

    seenValues.add(normalizedItem.value)
    prev.push(normalizedItem)

    return prev
  }, [])
}

// 外部只会回传 `{ value, type }`，这里负责把它还原成当前 UI 需要的输入值和已选项状态。
const syncSelectedItems = payload => {
  const nextTypeValue = (payload && payload.type) || (normalizedTypes.value[0] && normalizedTypes.value[0].value) || ''
  const nextType = normalizedTypes.value.find(item => item.value === nextTypeValue) || normalizedTypes.value[0] || null

  currentType.value = nextType

  if (!nextType) {
    selectedItems.value = []
    keyword.value = ''
    return
  }

  if (isTypeInput(nextType)) {
    selectedItems.value = []
    keyword.value = normalizeTypePayload(payload, nextType)
    return
  }

  const nextValue = normalizeTypePayload(payload, nextType)
  const nextValues = getTypeMode(nextType) === 'multiple' ? nextValue : (nextValue ? [nextValue] : [])
  const currentOptionsMap = new Map(normalizeOptions(nextType.options).map(item => [item.value, item]))
  const currentSelectedMap = new Map(selectedItems.value.map(item => [item.value, item]))

  selectedItems.value = nextValues.map(item => {
    const value = String(item)
    const matchedItem = currentOptionsMap.get(value) || currentSelectedMap.get(value)
    return matchedItem || {
      label: value,
      value
    }
  })
  keyword.value = ''
}

// 所有值变更都统一从这里向外发射，避免 `update:modelValue` / `update:value` 数据不一致。
const emitValue = () => {
  const currentTypeValue = (currentType.value && currentType.value.value) || ''
  let nextValue = ''

  if (isTypeInput(currentType.value)) {
    nextValue = keyword.value
  } else if (getTypeMode(currentType.value) === 'multiple') {
    nextValue = selectedItems.value.map(item => item.value)
  } else {
    nextValue = (selectedItems.value[0] && selectedItems.value[0].value) || ''
  }

  const payload = {
    type: currentTypeValue,
    value: nextValue
  }

  emit('update:modelValue', payload)
  emit('update:value', payload)
  emit('change', payload)
}

// 同步父组件传入的 v-model 值，确保外部更新时内部 UI 一起刷新。
watch(currentModelValue, value => {
  syncSelectedItems(value)
}, {
  deep: true,
  immediate: true
})

// 当 types 配置动态变化时，重新校准当前类型和已选值，避免组件停留在失效类型上。
watch(normalizedTypes, list => {
  if (!list.length) {
    currentType.value = null
    selectedItems.value = []
    keyword.value = ''
    return
  }

  const externalValue = currentModelValue.value
  const propsType = externalValue && externalValue.type
  const currentTypeValue = currentType.value && currentType.value.value
  const matchedType = list.find(item => item.value === (propsType || currentTypeValue))
  currentType.value = matchedType || list[0]

  syncSelectedItems({
    type: currentType.value.value,
    value: externalValue && externalValue.value
  })
}, {
  immediate: true
})

watch(() => props.appendTo, () => {
  teleportTarget.value = resolveTeleportTarget()
  if (isAnyPanelVisible.value) {
    scheduleSyncPanelPosition()
  }
}, {
  immediate: true
})

// 面板显隐、类型切换都会改变定位基准，需要及时刷新面板位置。
watch([showTypePanel, showResultPanel, isSelectType], () => {
  if (isAnyPanelVisible.value) {
    scheduleSyncPanelPosition()
  }
})

// 搜索结果可见数量变化后，面板实际高度也会变化，因此需要重新计算可用空间。
watch(() => visibleFilteredOptions.value.length, () => {
  if (showResultPanel.value && isSelectType.value) {
    scheduleSyncPanelPosition()
  }
})

// 类型列表变化会影响左侧类型面板的高度，同样需要重新定位。
watch(() => normalizedTypes.value.length, () => {
  if (showTypePanel.value) {
    scheduleSyncPanelPosition()
  }
})

// Teleport 后点击面板不再属于组件 DOM 子树，需要把根节点和两个面板都纳入外部点击判断。
const handleDocumentPointerDown = event => {
  const target = event.target
  if (
    rootRef.value?.contains(target) ||
    typePanelRef.value?.contains(target) ||
    resultPanelRef.value?.contains(target)
  ) {
    return
  }

  handleClickOutside()
}

// 浏览器窗口 resize，或任意祖先滚动时，都按当前触发区重新校准面板位置。
const handleWindowLayoutChange = () => {
  if (!isAnyPanelVisible.value) return
  syncPanelPosition()
}

// 控制左侧类型面板的展开收起，并在切换时关闭右侧结果面板。
const toggleTypePanel = () => {
  if (!normalizedTypes.value.length) return
  showTypePanel.value = !showTypePanel.value
  showResultPanel.value = false
  isInputFocus.value = false
  if (showTypePanel.value) {
    scheduleSyncPanelPosition()
  }
}

// 点击主区域时聚焦输入框；select 类型顺带展开结果面板。
const focusInput = () => {
  showTypePanel.value = false
  showResultPanel.value = isSelectType.value
  scheduleSyncPanelPosition()
  nextTick(() => {
    if (inputRef.value) {
      inputRef.value.focus()
    }
  })
}

// 输入框 focus 后统一进入“可输入/可筛选”状态，并刷新结果面板的位置。
const handleInputFocus = () => {
  isInputFocus.value = true
  showTypePanel.value = false
  showResultPanel.value = isSelectType.value
  scheduleSyncPanelPosition()
}

// input 类型直接回写输入值；select 类型则只更新前端筛选状态。
const handleKeywordInput = () => {
  if (!isSelectType.value) {
    emitValue()
    return
  }

  showResultPanel.value = true
  scheduleSyncPanelPosition()

  if (!isMultiple.value && selectedItems.value.length) {
    const selectedValue = selectedItems.value[0]
    // 单选 select 场景下继续输入，表示用户开始重新筛选，需要先清掉旧选中项。
    if (selectedValue && keyword.value !== selectedValue.label) {
      selectedItems.value = []
      emitValue()
    }
  }
}

// 处理下拉项选择：多选做勾选切换，单选直接覆盖当前值并关闭面板。
const selectItem = item => {
  if (!isSelectType.value) return

  const normalizedItem = getNormalizedOption(item)
  if (!normalizedItem) return

  if (isMultiple.value) {
    // 多选 select 再次点击同一项时视为取消勾选。
    const currentIndex = selectedItems.value.findIndex(option => option.value === normalizedItem.value)

    if (currentIndex > -1) {
      selectedItems.value.splice(currentIndex, 1)
    } else {
      selectedItems.value.push(normalizedItem)
    }

    keyword.value = ''
    emitValue()
    nextTick(() => {
      if (inputRef.value) {
        inputRef.value.focus()
      }
    })
    return
  }

  selectedItems.value = [normalizedItem]
  keyword.value = ''
  showResultPanel.value = false
  isInputFocus.value = false
  emitValue()
}

// 切换检索类型时清空旧条件，避免 input/select 两种模式之间互相污染。
const changeType = item => {
  if (currentType.value && item.value === currentType.value.value) {
    showTypePanel.value = false
    isInputFocus.value = false
    return
  }

  currentType.value = item
  selectedItems.value = []
  keyword.value = ''
  showTypePanel.value = false
  showResultPanel.value = item.type === 'select'
  scheduleSyncPanelPosition()

  const payload = createEmptyPayload(item)
  emit('update:modelValue', payload)
  emit('update:value', payload)
  emit('change', payload)
}

// 删除单个已选项，多用于 select multiple 或单选 tag 清除。
const removeItem = value => {
  selectedItems.value = selectedItems.value.filter(item => item.value !== value)
  emitValue()
}

// 清空当前类型下的所有输入或已选内容，并把空值同步给父组件。
const clearValue = () => {
  keyword.value = ''
  selectedItems.value = []
  emitValue()
}

// select 类型按回车默认选中当前过滤结果中的第一项。
const handleEnter = () => {
  if (!isSelectType.value || !filteredOptions.value.length) return
  selectItem(filteredOptions.value[0])
}

// input 类型按回车时单独抛出 enter 事件，方便父组件决定何时发起查询。
const handleInputEnter = () => {
  if (isSelectType.value) return
  emit('enter', {
    type: (currentType.value && currentType.value.value) || '',
    value: keyword.value
  })
}

// 判断某个 option 是否已被选中，用于列表勾选状态展示。
const isSelected = value => selectedValueSet.value.has(String(value))

// 点击组件外部时统一关闭所有浮层和聚焦态，避免 Teleport 面板残留在页面上。
const handleClickOutside = () => {
  showTypePanel.value = false
  showResultPanel.value = false
  isInputFocus.value = false
}

onMounted(() => {
  teleportTarget.value = resolveTeleportTarget()
  document.addEventListener('mousedown', handleDocumentPointerDown)
  window.addEventListener('resize', handleWindowLayoutChange)
  window.addEventListener('scroll', handleWindowLayoutChange, true)
})

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', handleDocumentPointerDown)
  window.removeEventListener('resize', handleWindowLayoutChange)
  window.removeEventListener('scroll', handleWindowLayoutChange, true)
})
</script>

<style lang="scss" scoped>
.search-select-input {
  position: relative;
  width: 100%;
  font-size: 14px;
  line-height: 20px;
  display: inline-flex;

  &__inner {
    display: flex;
    align-items: stretch;
    width: 100%;
    height: 32px;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
  }

  &__type {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 12px;
    border: 1px solid var(--color-border-1);
    border-top-left-radius: var(--el-button-radius);
    border-bottom-left-radius: var(--el-button-radius);
    background: var(--color-bg-1);
    cursor: pointer;
    user-select: none;
    width: 110px;
    box-sizing: border-box;
  }

  &__type-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-right: 14px;
    line-height: 22px;
  }

  &__type-arrow {
    color: #a0a8b8;
    transition: transform 0.2s ease;
  }

  &__type.is-open &__type-arrow {
    transform: rotate(180deg);
  }

  &__main {
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 0;
    padding: 0 6px 0 12px;
    min-width: 120px;
    cursor: text;
    border: 1px solid var(--color-border-1);
    border-top-right-radius: var(--el-button-radius);
    border-bottom-right-radius: var(--el-button-radius);
    background: var(--color-bg-1);
    margin-left: -1px;
    position: relative;
    box-sizing: border-box;
  }

  &__type.is-open,
  &.is-focus &__main {
    z-index: 1;
    border-color: var(--color-primary-7);
  }

  &__tags {
    display: flex;
    align-items: center;
    flex: 1;
    gap: 4px;
    min-width: 0;
    flex-wrap: nowrap;
    position: relative;
    overflow: hidden;
  }

  &__single {
    display: flex;
    align-items: center;
    flex: 1;
    gap: 4px;
    min-width: 0;
    overflow: hidden;
    min-height: 32px;
    position: relative;
  }
  &.is-focus &__single {
    color: var(--color-text-3);
  }

  &__tag {
    display: inline-flex;
    align-items: center;
    max-width: 102px;
    padding: 2px 8px;
    height: 24px;
    font-size: 12px;
    border: 1px solid var(--color-border-1);
    border-radius: var(--el-button-radius);
    background: var(--color-bg-3);
    color: var(--color-text-1);
    margin-top: 4px;
    margin-bottom: 4px;
    flex-shrink: 0;
    box-sizing: border-box;
    &:hover {
      color: var(--color-primary-7);
      cursor: pointer;
    }
    &--count {
      font-size: 12px;
    }

    &--single {
      max-width: 100%;
    }
  }

  &__tag-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__tag-close {
    margin-left: 6px;
    color: var(--color-text-4);
    cursor: pointer;
    &:hover {
      color: var(--color-text-1);
    }
  }

  &__input {
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    padding: 0;
    height: 32px;
    background: transparent;
    color: var(--color-text-1);
    font-size: 14px;
  }

  &__input--multiple {
    min-width: 1px;
  }

  &__input--single {
    width: 100%;
    position: absolute;
    left: 0;
    top: 0;
    z-index: 1;
  }

  &__input::placeholder {
    color: #AEB2B8;
  }

  &__suffix {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    color: var(--color-text-4);
    flex-shrink: 0;

    .el-icon {
      cursor: pointer;
    }

    .is-loading {
      animation: rotating 1s linear infinite;
    }
    &:hover {
      color: var(--color-text-1);
    }
  }

  &__type-panel,
  &__panel {
    background: var(--color-bg-1);
    border-radius: var(--el-button-radius);
    box-shadow: var(--shadow);
    z-index: 1003;
    overflow: hidden;
  }

  &__type-panel {
    max-height: 220px;
    overflow-y: auto;
  }

  &__panel {
    max-height: 220px;
    overflow-y: auto;
  }

  &__type-option,
  &__option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 5px 12px;
    font-size: 14px;
    cursor: pointer;
    line-height: 22px;
    transition: background-color 0.2s ease, color 0.2s ease;
  }

  &__type-option:hover,
  &__option:hover {
    background: var(--color-bg-3);
  }

  &__type-option.is-active,
  &__option.is-selected {
    color: var(--color-primary);
    .search-select-input__option-label {
      color: var(--color-text-1);
      font-weight: 500;
    }
  }
  &__type-option.is-active span {
    color: var(--color-text-1);
    font-weight: 500;
  }

  &__option-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__option-check {
    margin-left: 12px;
    flex-shrink: 0;
  }

  &__status {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 120px;
    padding: 0 16px;
    color: var(--color-text-4);
  }

  &__status--more {
    min-height: 34px;
    padding: 6px 12px;
    font-size: 12px;
    line-height: 18px;
    text-align: center;
    border-top: 1px solid var(--color-border-1);
    box-sizing: border-box;
  }
}

:global(.search-select-input__selected-tooltip) {
  padding: 12px 0 !important;
  border: none !important;
  border-radius: 2px !important;
  background: var(--color-bg-1) !important;
  box-shadow: 0 4px 16px rgba(13, 35, 68, 0.14) !important;
}

:global(.search-select-input__selected-tooltip .search-select-input__selected-tooltip-content) {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 12px;
  max-width: 161px;
  max-height: 220px;
  overflow: auto;
}

:global(.search-select-input__selected-tooltip .search-select-input__selected-tooltip-item) {
  display: flex;
  align-items: center;
  max-width: 100%;
}

:global(.search-select-input__selected-tooltip .search-select-input__selected-tooltip-label) {
  display: block;

  height: 24px;
  padding: 2px 8px;
  border: 1px solid var(--color-border-1);
  border-radius: var(--el-button-radius);
  background: var(--color-bg-3);
  color: var(--color-text-1);
  font-size: 12px;

  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  box-sizing: border-box;
}

@keyframes rotating {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}
</style>
